import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["emailPanel", "overlay", "passwordPanel", "passwordOverlay"]

    connect() {
        this.addPasswordValidationListeners()
        
        this.handleTurboRender = this.handleTurboRender.bind(this)
        document.addEventListener('turbo:render', this.handleTurboRender)
        document.addEventListener('turbo:frame-render', this.handleTurboRender)
        document.addEventListener('turbo:before-stream-render', this.handleTurboRender)
        document.addEventListener('turbo:after-stream-render', this.handleTurboRender)
    }
    
    disconnect() {
        try {
            if (this.hasEmailPanelTarget) {
                this.emailPanelTarget.style.right = '-100%'
            }
            if (this.hasOverlayTarget) {
                this.overlayTarget.classList.add('hidden')
            }
            if (this.hasPasswordPanelTarget) {
                this.passwordPanelTarget.style.right = '-100%'
            }
            if (this.hasPasswordOverlayTarget) {
                this.passwordOverlayTarget.classList.add('hidden')
            }
        } catch (error) {
            console.warn('Could not reset panel or overlay state during disconnect')
        }
        
        document.removeEventListener('turbo:render', this.handleTurboRender)
        document.removeEventListener('turbo:frame-render', this.handleTurboRender)
        document.removeEventListener('turbo:before-stream-render', this.handleTurboRender)
        document.removeEventListener('turbo:after-stream-render', this.handleTurboRender)
        
        document.body.style.overflow = ''
    }

    handleEmailSubmit(event) {
        const form = event.target
        const newEmail = form.querySelector('#id_new_email').value.trim()
        const confirmEmail = form.querySelector('#id_confirm_email').value.trim()
        
        this.clearErrorMessage(form)
        
        if (!this.isValidEmail(newEmail)) {
            this.showErrorMessage(form, 'Please enter a valid email address.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        if (newEmail !== confirmEmail) {
            this.showErrorMessage(form, 'Email addresses do not match.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = 'Updating Email...'
            submitButton.disabled = true
        }
        
    }

    handlePasswordSubmit(event) {
        const form = event.target
        const currentPassword = form.querySelector('#id_current_password').value.trim()
        const newPassword = form.querySelector('#id_new_password').value // Keep original for validation
        const confirmPassword = form.querySelector('#id_confirm_password').value.trim()
        
        this.clearErrorMessage(form)
        
        if (!currentPassword) {
            this.showErrorMessage(form, 'Current password is required.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        const passwordValidation = this.validatePasswordStrength(newPassword)
        if (!passwordValidation.valid) {
            this.showErrorMessage(form, passwordValidation.message)
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        const trimmedNewPassword = newPassword.trim()
        if (trimmedNewPassword !== confirmPassword) {
            this.showErrorMessage(form, 'Passwords do not match.')
            this.resetSubmitButton(form, 'Save')
            event.preventDefault()
            return
        }
        
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = 'Changing Password...'
            submitButton.disabled = true
        }

    }

    handleTurboRender() {
        setTimeout(() => {
            this.addPasswordValidationListeners()
        }, 50)
    }

    handlePasswordSubmitEnd(event) {
        const success = event.detail?.success
        
        if (!success) {
            // Reset submit button on failure
            const form = document.getElementById('reset-password-form')
            if (form) {
                this.resetSubmitButton(form, 'Save')
            }
            
            // Re-add validation listeners after a delay
            setTimeout(() => {
                this.addPasswordValidationListeners()
            }, 300)
        }
        // If success is true, don't reset the button (keep it as "Changing Password...")
    }

    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    validatePasswordStrength(password) {
        // For passwords 15-64 characters, check if whitespace-only
        if (password.length >= 15 && password.length <= 64 && password.trim() === '') {
            return { valid: false, message: 'Please specify password.' }
        }
        
        return { valid: true }
    }

    showErrorMessage(form, message) {
        let errorContainer = form.querySelector('.frontend-validation-error')
        if (!errorContainer) {
            errorContainer = document.createElement('div')
            errorContainer.className = 'frontend-validation-error w-full max-w-[540px] bg-[#FFEBEE] border border-[#D32F2F] rounded-[4px] py-3 px-4 mt-2'
            
            const fieldsContainer = Array.from(form.querySelectorAll('div')).find(div => 
                div.classList.contains('w-full') && 
                div.classList.contains('flex') && 
                div.classList.contains('flex-col') && 
                div.querySelector('input')
            )
            
            if (fieldsContainer) {
                form.insertBefore(errorContainer, fieldsContainer.nextSibling)
            } else {
                form.appendChild(errorContainer)
            }
        }
        
        errorContainer.innerHTML = `
            <div class="flex items-start gap-3">
                <span class="w-5 h-5 text-[#D32F2F] flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </span>
                <p class="text-sm text-[#D32F2F] font-medium">${message}</p>
            </div>
        `
        errorContainer.style.display = 'block'
    }

    clearErrorMessage(form) {
        // Clear frontend validation errors
        const errorContainer = form.querySelector('.frontend-validation-error')
        if (errorContainer) {
            errorContainer.style.display = 'none'
        }
        
        // Clear server-side errors (like password breach errors)  
        const serverErrorDiv = document.getElementById('server-error-message')
        if (serverErrorDiv) {
            serverErrorDiv.style.display = 'none'
        }
    }

    resetSubmitButton(form, originalText) {
        const submitButton = form.querySelector('button[type="submit"]')
        if (submitButton) {
            submitButton.innerHTML = originalText
            submitButton.disabled = false
        }
    }

    resetEmailForm() {
        const emailForm = document.getElementById('edit-email-form')
        if (!emailForm) return
        
        const newEmailInput = emailForm.querySelector('#id_new_email')
        const confirmEmailInput = emailForm.querySelector('#id_confirm_email')
        
        if (newEmailInput) {
            newEmailInput.value = ''
        }
        if (confirmEmailInput) {
            confirmEmailInput.value = ''
        }
        
        this.clearErrorMessage(emailForm)
    }

    resetPasswordForm() {
        const passwordForm = document.getElementById('reset-password-form')
        if (!passwordForm) return
        
        const currentPasswordInput = passwordForm.querySelector('#id_current_password')
        const newPasswordInput = passwordForm.querySelector('#id_new_password')
        const confirmPasswordInput = passwordForm.querySelector('#id_confirm_password')
        
        if (currentPasswordInput) {
            currentPasswordInput.value = ''
        }
        if (newPasswordInput) {
            newPasswordInput.value = ''
            this.hideInlinePasswordError(newPasswordInput)
        }
        if (confirmPasswordInput) {
            confirmPasswordInput.value = ''
        }
        
        // Clear validation classes from criteria
        const minLengthElement = document.getElementById('minLength')
        const maxLengthElement = document.getElementById('maxLength')
        
        if (minLengthElement) {
            this.resetValidationClasses(minLengthElement)
        }
        if (maxLengthElement) {
            this.resetValidationClasses(maxLengthElement)
        }
        
        this.clearErrorMessage(passwordForm)
    }

    addEmailValidationListeners() {
        const emailForm = document.getElementById('edit-email-form')
        
        if (emailForm) {
            const newEmailInput = emailForm.querySelector('#id_new_email')
            const confirmEmailInput = emailForm.querySelector('#id_confirm_email')
            
            if (newEmailInput) {
                newEmailInput.addEventListener('input', () => this.clearErrorMessage(emailForm))
            }
            if (confirmEmailInput) {
                confirmEmailInput.addEventListener('input', () => this.clearErrorMessage(emailForm))
            }
        }
    }

    addPasswordValidationListeners() {
        const passwordForm = document.getElementById('reset-password-form')
        
        if (passwordForm) {
            const currentPasswordInput = passwordForm.querySelector('#id_current_password')
            const newPasswordInput = passwordForm.querySelector('#id_new_password')
            const confirmPasswordInput = passwordForm.querySelector('#id_confirm_password')
            
            if (currentPasswordInput) {
                if (this.currentPasswordHandler) {
                    currentPasswordInput.removeEventListener('input', this.currentPasswordHandler)
                }
                this.currentPasswordHandler = () => {
                    this.clearErrorMessage(passwordForm)
                    this.validatePasswordFormAndToggleSaveButton()
                }
                currentPasswordInput.addEventListener('input', this.currentPasswordHandler)
            }
            
            if (newPasswordInput) {
                if (this.newPasswordInputHandler) {
                    newPasswordInput.removeEventListener('input', this.newPasswordInputHandler)
                }
                if (this.newPasswordBlurHandler) {
                    newPasswordInput.removeEventListener('blur', this.newPasswordBlurHandler)
                }
                
                this.newPasswordInputHandler = (event) => {
                    this.clearErrorMessage(passwordForm)
                    this.validatePasswordCriteriaRealTime(event.target.value)
                    this.validatePasswordInline(event.target.value, newPasswordInput)
                    this.validatePasswordFormAndToggleSaveButton()
                }
                this.newPasswordBlurHandler = (event) => {
                    this.validatePasswordInline(event.target.value, newPasswordInput)
                    this.validatePasswordFormAndToggleSaveButton()
                }
                
                newPasswordInput.addEventListener('input', this.newPasswordInputHandler)
                newPasswordInput.addEventListener('blur', this.newPasswordBlurHandler)
                
                if (newPasswordInput.value) {
                    this.validatePasswordCriteriaRealTime(newPasswordInput.value)
                    this.validatePasswordFormAndToggleSaveButton()
                }
            }
            
            if (confirmPasswordInput) {
                if (this.confirmPasswordHandler) {
                    confirmPasswordInput.removeEventListener('input', this.confirmPasswordHandler)
                }
                this.confirmPasswordHandler = () => {
                    this.clearErrorMessage(passwordForm)
                    this.validatePasswordFormAndToggleSaveButton()
                }
                confirmPasswordInput.addEventListener('input', this.confirmPasswordHandler)
            }
        }
    }

    validatePasswordCriteriaRealTime(password) {
        const minLengthElement = document.getElementById('minLength')
        const maxLengthElement = document.getElementById('maxLength')
        
        if (!minLengthElement || !maxLengthElement) return
        
        this.resetValidationClasses(minLengthElement)
        this.resetValidationClasses(maxLengthElement)
        
        if (password.length >= 15) {
            minLengthElement.classList.add('valid-pass')
        } else if (password.length > 0) {
            minLengthElement.classList.add('invalid-pass')
        }
        
        if (password.length <= 64 && password.length > 0) {
            maxLengthElement.classList.add('valid-pass')
        } else if (password.length > 64) {
            maxLengthElement.classList.add('invalid-pass')
        }
    }

    validatePasswordInline(password, inputElement) {
        if (password.length === 0) {
            this.hideInlinePasswordError(inputElement)
            return
        }
        
        const validation = this.validatePasswordStrength(password)
        
        if (!validation.valid) {
            this.showInlinePasswordError(inputElement, validation.message)
        } else {
            this.hideInlinePasswordError(inputElement)
        }
    }

    showInlinePasswordError(inputElement, message) {
        if (!inputElement || !inputElement.parentNode) return
        
        let inlineError = inputElement.parentNode.querySelector('.password-inline-error')
        
        if (!inlineError) {
            inlineError = document.createElement('div')
            inlineError.className = 'password-inline-error show'
            
            inputElement.parentNode.insertBefore(inlineError, inputElement.nextSibling)
        } else {
            inlineError.classList.add('show')
        }
        
        inlineError.textContent = message
        this.forceReflow(inlineError)
    }

    hideInlinePasswordError(inputElement) {
        if (!inputElement || !inputElement.parentNode) return
        
        const inlineError = inputElement.parentNode.querySelector('.password-inline-error')
        if (inlineError) {
            inlineError.classList.remove('show')
            this.forceReflow(inlineError)
        }
    }

    forceReflow(element) {
        // Reading layout metrics forces a reflow so CSS transitions apply
        return element.offsetHeight
    }

    validatePasswordFormAndToggleSaveButton() {
        const passwordForm = document.getElementById('reset-password-form')
        if (!passwordForm) return
        
        const currentPasswordInput = passwordForm.querySelector('#id_current_password')
        const newPasswordInput = passwordForm.querySelector('#id_new_password')
        const confirmPasswordInput = passwordForm.querySelector('#id_confirm_password')
        const saveButton = document.querySelector('button[form="reset-password-form"]')
        
        if (!saveButton) return
        
        const currentPassword = currentPasswordInput ? currentPasswordInput.value.trim() : ''
        const newPassword = newPasswordInput ? newPasswordInput.value : ''
        const confirmPassword = confirmPasswordInput ? confirmPasswordInput.value.trim() : ''
        
        if (!currentPassword) {
            saveButton.disabled = true
            return
        }
        
        const passwordValidation = this.validatePasswordStrength(newPassword)
        if (!passwordValidation.valid) {
            saveButton.disabled = true
            return
        }

      if (newPassword === "" || newPassword.length < 15 || newPassword.length > 64) {
        saveButton.disabled = true;
        return;
      }
        
        saveButton.disabled = false
    }

    resetValidationClasses(element) {
        element.classList.remove('valid-pass', 'invalid-pass')
    }

    addValidationListeners() {
        this.addEmailValidationListeners()
        this.addPasswordValidationListeners()
    }

    toggleBackToTop(hide) {
        const backToTopContainer = document.getElementById('back-to-top-container')
        if (backToTopContainer) {
            backToTopContainer.classList.toggle('hidden', hide)
        }
    }

    closeSearchBar() {
        const navigationController = this.application.getControllerForElementAndIdentifier(
            document.querySelector('[data-controller="navigation"]'),
            "navigation"
        )
        
        if (navigationController && navigationController.hasSearchBarTarget) {
            const searchBar = navigationController.searchBarTarget
            
            if (searchBar && searchBar.style.display !== 'none') {
                navigationController.toggleSearchBar()
            }
        }
    }

    openEmail(event) {
        if (event) event.preventDefault()
        
        this.closeSearchBar()
        
        if (this.hasEmailPanelTarget) {
            this.emailPanelTarget.style.right = '0'
        }
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.remove('hidden')
        }
        document.body.style.overflow = 'hidden'
        this.toggleBackToTop(true)
        
        setTimeout(() => {
            this.addEmailValidationListeners()
        }, 100)
    }

    closeEmail(event) {
        if (event) event.preventDefault()

        if (this.hasEmailPanelTarget) {
            this.emailPanelTarget.style.right = '-100%'
        }
        if (this.hasOverlayTarget) {
            this.overlayTarget.classList.add('hidden')
        }
        document.body.style.overflow = ''
        this.toggleBackToTop(false)
        this.resetEmailForm()
    }

    openPassword(event) {
        if (event) event.preventDefault()
        
        this.closeSearchBar()
        
        if (this.hasPasswordPanelTarget) {
            this.passwordPanelTarget.style.right = '0'
        }
        if (this.hasPasswordOverlayTarget) {
            this.passwordOverlayTarget.classList.remove('hidden')
        }
        document.body.style.overflow = 'hidden'
        this.toggleBackToTop(true)
        
        setTimeout(() => {
            this.addPasswordValidationListeners()
            this.validatePasswordFormAndToggleSaveButton()
        }, 100)
    }

    closePassword(event) {
        if (event) event.preventDefault()

        if (this.hasPasswordPanelTarget) {
            this.passwordPanelTarget.style.right = '-100%'
        }
        if (this.hasPasswordOverlayTarget) {
            this.passwordOverlayTarget.classList.add('hidden')
        }
        document.body.style.overflow = ''
        this.toggleBackToTop(false)
        this.resetPasswordForm()
    }
}
